import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useId, } from 'react';
import { cn } from '../cn.js';
import { ChevronDown, StatusGlyph } from './icons.js';
/**
 * A labelled control with its error attached (§06).
 *
 * The error is wired through `aria-describedby` and `aria-invalid` rather than
 * painted red, so it is announced rather than merely visible — colour alone
 * fails WCAG 1.4.1, and the people using this console skew towards the age
 * where that stops being a technicality.
 *
 * Errors render inline beneath the field and never as a toast: a message about
 * a field belongs beside the field, where the person is already looking.
 */
const CONTROL = [
    'w-full rounded-md border bg-surface px-3',
    'min-h-web-primary font-sans text-md text-ink placeholder:text-ink-3',
    'transition-colors duration-fast ease-calm',
    'disabled:cursor-not-allowed disabled:opacity-40',
].join(' ');
function Shell({ id, label, hint, error, children, }) {
    return (_jsxs("div", { className: "grid gap-1", children: [_jsx("label", { htmlFor: id, className: "text-ink-2 text-sm font-semibold", children: label }), hint && (_jsx("p", { id: `${id}-hint`, className: "text-ink-3 text-xs", children: hint })), children, error && (_jsxs("p", { id: `${id}-error`, "aria-live": "polite", className: "text-critical flex items-start gap-1.5 text-xs font-medium", children: [_jsx(StatusGlyph, { name: "triangle", className: "mt-0.5 h-3.5 w-3.5" }), error] }))] }));
}
const describedBy = (id, hint, error) => [error && `${id}-error`, hint && `${id}-hint`].filter(Boolean).join(' ') || undefined;
export const Field = forwardRef(function Field({ label, error, hint, className, id, ...rest }, ref) {
    const generated = useId();
    const fieldId = id ?? generated;
    return (_jsx(Shell, { id: fieldId, label: label, hint: hint, error: error, children: _jsx("input", { ref: ref, id: fieldId, "aria-invalid": error ? true : undefined, "aria-describedby": describedBy(fieldId, hint, error), className: cn(CONTROL, error ? 'border-critical' : 'border-line-strong', className), ...rest }) }));
});
export const SelectField = forwardRef(function SelectField({ label, error, hint, className, id, children, ...rest }, ref) {
    const generated = useId();
    const fieldId = id ?? generated;
    return (_jsx(Shell, { id: fieldId, label: label, hint: hint, error: error, children: _jsxs("div", { className: "relative", children: [_jsx("select", { ref: ref, id: fieldId, "aria-invalid": error ? true : undefined, "aria-describedby": describedBy(fieldId, hint, error), className: cn(CONTROL, 'appearance-none pr-10', error ? 'border-critical' : 'border-line-strong', className), ...rest, children: children }), _jsx(ChevronDown, { className: "text-ink-3 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" })] }) }));
});
//# sourceMappingURL=field.js.map